import axios from 'axios';
import { API_ENDPOINTS } from './apiEndpoints';

// Create an Axios instance
const axiosInstance = axios.create({
  // baseURL: window.location.origin,
  baseURL: API_ENDPOINTS.BASE_URL,
  // timeout: 10000, // Timeout in milliseconds
});

// Create a separate Axios instance for refresh token requests (without Authorization header)
const refreshAxiosInstance = axios.create({
  baseURL: API_ENDPOINTS.BASE_URL,
  // baseURL: window.location.origin,
  // timeout: 10000, // Timeout in milliseconds
});

// Token management functions
const tokenManager = {
  // Store tokens in localStorage
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },

  // Get access token
  getAccessToken: () => {
    return localStorage.getItem('accessToken');
  },

  // Get refresh token
  getRefreshToken: () => {
    return localStorage.getItem('refreshToken');
  },

  // Clear all tokens
  clearTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  // Check if tokens exist
  hasTokens: () => {
    return !!(tokenManager.getAccessToken() && tokenManager.getRefreshToken());
  }
};

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Loading state for token refresh
let refreshLoadingCallback = null;

// Set loading callback for UI updates
const setRefreshLoadingCallback = (callback) => {
  refreshLoadingCallback = callback;
};

// Show loading state
const showRefreshLoading = () => {
  if (refreshLoadingCallback) {
    refreshLoadingCallback(true);
  }
};

// Hide loading state
const hideRefreshLoading = () => {
  if (refreshLoadingCallback) {
    refreshLoadingCallback(false);
  }
};

// Process failed requests queue
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};


// Function to refresh access token
const refreshAccessToken = async () => {
  try {
    showRefreshLoading();
    const refreshToken = tokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // Use separate axios instance without Authorization header for refresh token request
    console.log('🔄 Refreshing access token...', {
      endpoint: API_ENDPOINTS.POST_refresh_access_token,
      hasRefreshToken: !!refreshToken
    });
    
    const response = await refreshAxiosInstance.post(API_ENDPOINTS.POST_refresh_access_token, {
      refresh: refreshToken
    });

    if (response.data.accessToken) {
      tokenManager.setTokens(response.data.accessToken, refreshToken);
      return response.data.accessToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    
    // Clear all tokens and user data from localStorage
    tokenManager.clearTokens();
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('logged_data');
    
    // Hide loading first
    hideRefreshLoading();
    
    // Show error message
    // alert('Your session has expired. Please login again.');
    
    // Redirect to login page
    window.location.href = '/login';
    throw error;
  } finally {
    hideRefreshLoading();
  }
};

// Request interceptor to add token and user_id automatically
axiosInstance.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add user_id to all requests
    const userId = localStorage.getItem('user_id');
    if (userId) {
      // Add user_id as query parameter for GET requests
      if (config.method === 'get' || config.method === 'delete') {
        config.params = {
          ...config.params,
          auth_user: userId
        };
      }
      // Add user_id to request body for POST/PUT requests
      else if (config.method === 'post' || config.method === 'put') {
        if (config.data) {
          // If data is FormData, append auth_user
          if (config.data instanceof FormData) {
            config.data.append('auth_user', userId);
          }
          // If data is object, add auth_user
          else if (typeof config.data === 'object') {
            config.data = {
              ...config.data,
              auth_user: userId
            };
          }
        } else {
          config.data = { auth_user: userId };
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if this is already a refresh token request
      if (originalRequest.url?.includes('refresh-access-token') || originalRequest.url?.includes('refresh_access_token')) {
        // This is a refresh token request that failed, clear everything and redirect
        tokenManager.clearTokens();
        localStorage.removeItem('user_id');
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('logged_data');
        hideRefreshLoading();
        // alert('Your session has expired. Please login again.');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Generic reusable functions
const apiService = {
  get: async (endpoint) => {
    try {
      const response = await axiosInstance.get(endpoint);
      return response.data;
    } catch (error) {
      console.error(`GET ${endpoint} failed:`, error);
      throw error.response?.data || error.message;
    }
  },

  post: async (endpoint, data, contentType = 'application/json') => {
    try {
      console.log("🚀 ~ post: ~ endpoint:", endpoint)
      const response = await axiosInstance.post(endpoint, data, {
        headers: { 'Content-Type': contentType }
      });
      
      return response.data;
    } catch (error) {
      console.error(`POST ${endpoint} failed:`, error);
      throw error.response?.data || error.message;
    }
  },

  put: async (endpoint, data, contentType = 'application/json') => {
    try {
      const response = await axiosInstance.put(endpoint, data, {
        headers: { 'Content-Type': contentType }
      });
      return response.data;
    } catch (error) {
      console.error(`PUT ${endpoint} failed:`, error);
      throw error.response?.data || error.message;
    }
  },

  delete: async (endpoint) => {
    try {
      const response = await axiosInstance.delete(endpoint);
      return response.data;
    } catch (error) {
      console.error(`DELETE ${endpoint} failed:`, error);
      throw error.response?.data || error.message;
    }
  },


  // Specialized method for file downloads
  downloadFile: async (endpoint, filename = 'download.xlsx') => {
    try {
      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error(`Download ${endpoint} failed:`, error);
      throw error.response?.data || error.message;
    }
  },

  /** Filename from Content-Disposition only (backend-controlled). */
  parseFilenameFromContentDisposition(header) {
    if (!header || typeof header !== 'string') return null;
    const utf8Match = header.match(/filename\*=UTF-8''([^;\n]+)/i);
    if (utf8Match) {
      try {
        return decodeURIComponent(utf8Match[1].trim().replace(/^"+|"+$/g, ''));
      } catch {
        return utf8Match[1];
      }
    }
    const quoted = header.match(/filename="([^"]+)"/i);
    if (quoted) return quoted[1];
    const unquoted = header.match(/filename=([^;\n]+)/i);
    if (unquoted) {
      return unquoted[1].trim().replace(/^"|"$/g, '');
    }
    return null;
  },

  /** POST export — arraybuffer preserves binary bytes; no client-side filename. */
  async postExportBlob(endpoint, data, contentType = 'application/json') {
    return axiosInstance.post(endpoint, data, {
      headers: { 'Content-Type': contentType },
      responseType: 'arraybuffer',
    });
  },

  /** Download using server filename from Content-Disposition only. */
  downloadBlobFromExportResponse(response) {
    const raw = response.data;
    const disposition =
      response.headers['content-disposition'] || response.headers['Content-Disposition'];
    const filename = this.parseFilenameFromContentDisposition(disposition);
    const mimeHeader =
      response.headers['content-type'] ||
      response.headers['Content-Type'] ||
      '';
    const mime = (mimeHeader.split(';')[0] || '').trim() || 'application/octet-stream';

    let blob;
    if (raw instanceof ArrayBuffer) {
      blob = new Blob([raw], { type: mime });
    } else if (raw instanceof Blob) {
      blob =
        mime && mime !== 'application/octet-stream' && (!raw.type || raw.type === 'application/octet-stream')
          ? new Blob([raw], { type: mime })
          : raw;
    } else {
      blob = new Blob([raw], { type: mime });
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    if (filename) {
      link.download = filename;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  async getMessageIfExportBodyIsJsonError(data) {
    if (data instanceof ArrayBuffer) {
      if (data.byteLength === 0) return null;
      const text = new TextDecoder('utf-8').decode(data);
      const trimmed = text.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
      try {
        const errorData = JSON.parse(text);
        if (errorData.success === false || errorData.error || errorData.detail) {
          let msg = errorData.message || errorData.error || errorData.detail || 'Export failed';
          if (typeof msg === 'string' && msg.includes("can't multiply sequence")) {
            msg =
              'Server error: Invalid data format in order items. Our team has been notified. Please try again later or contact support.';
          }
          return msg;
        }
      } catch {
        return null;
      }
      return null;
    }
    if (data instanceof Blob) {
      if (!data.type || !data.type.includes('application/json')) return null;
      const text = await data.text();
      try {
        const errorData = JSON.parse(text);
        if (errorData.success === false || errorData.error) {
          let msg = errorData.message || errorData.error || errorData.detail || 'Export failed';
          if (typeof msg === 'string' && msg.includes("can't multiply sequence")) {
            msg =
              'Server error: Invalid data format in order items. Our team has been notified. Please try again later or contact support.';
          }
          return msg;
        }
      } catch {
        return null;
      }
      return null;
    }
    return null;
  },

  async getExportErrorMessage(error) {
    const data = error?.response?.data;
    if (data instanceof ArrayBuffer && data.byteLength > 0) {
      try {
        const text = new TextDecoder('utf-8').decode(data);
        const trimmed = text.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          const j = JSON.parse(text);
          return j.message || j.error || j.detail || 'Export failed';
        }
      } catch {
        /* ignore */
      }
    }
    if (data instanceof Blob && data.type?.includes('application/json')) {
      try {
        const text = await data.text();
        const j = JSON.parse(text);
        return j.message || j.error || j.detail || 'Export failed';
      } catch {
        /* ignore */
      }
    }
    return error?.message || 'Failed to export data. Please try again.';
  },

  // Token management functions
  setTokens: (accessToken, refreshToken) => {
    tokenManager.setTokens(accessToken, refreshToken);
  },

  getAccessToken: () => {
    return tokenManager.getAccessToken();
  },

  getRefreshToken: () => {
    return tokenManager.getRefreshToken();
  },

  clearTokens: () => {
    tokenManager.clearTokens();
  },

  hasTokens: () => {
    return tokenManager.hasTokens();
  },

  // Login function that automatically stores tokens and user data
  login: async (endpoint, data) => {
    try {
      const response = await axiosInstance.post(endpoint, data);
      
      if (response.data?.success && response.data?.data) {
        const { accessToken, refreshToken, user_id, user_type, first_name, last_name, company_details } = response.data.data;
        
        // Store tokens
        if (accessToken && refreshToken) {
          tokenManager.setTokens(accessToken, refreshToken);
        }
        
        // Store user data
        if (user_id) {
          localStorage.setItem('user_id', user_id);
          localStorage.setItem('username', `${first_name || ''} ${last_name || ''}`.trim());
          localStorage.setItem('role', user_type);
          localStorage.setItem('logged_data', JSON.stringify(response.data));
          
          // Store company details if user is Company type
          if (user_type === 'Company' && company_details) {
            localStorage.setItem('company_details', JSON.stringify(company_details));
            localStorage.setItem('company_id', company_details.id);
          }
        }
      }
      
      return response.data;
    } catch (error) {
      console.error(`Login ${endpoint} failed:`, error);
      throw error.response?.data || error.message;
    }
  },

  // Set loading callback for token refresh UI
  setRefreshLoadingCallback: (callback) => {
    setRefreshLoadingCallback(callback);
  },

  // Logout function that clears all data
  logout: () => {
    tokenManager.clearTokens();
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('logged_data');
    localStorage.removeItem('company_details');
    localStorage.removeItem('company_id');
    // window.location.href = '/login';
  },

  // Get current user ID
  getCurrentUserId: () => {
    return localStorage.getItem('user_id');
  },

  // Get current user data
  getCurrentUser: () => {
    const loggedData = localStorage.getItem('logged_data');
    return loggedData ? JSON.parse(loggedData) : null;
  },

  // Get company details (for Company users)
  getCompanyDetails: () => {
    const companyDetails = localStorage.getItem('company_details');
    return companyDetails ? JSON.parse(companyDetails) : null;
  },

  // Get company ID (for Company users)
  getCompanyId: () => {
    return localStorage.getItem('company_id');
  },

  // Check if user is Company type
  isCompanyUser: () => {
    const role = localStorage.getItem('role');
    return role === 'Company';
  },

  // Check if user is Admin type
  isAdminUser: () => {
    const role = localStorage.getItem('role');
    return role === 'Admin';
  }
}

export default apiService;
